import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Home, BookOpen, MessageCircle, BarChart3, Users, Music, TrendingUp, Trophy, Sparkles, Globe, LogOut, User, Info, Bell, Heart, Mic, Shield, Inbox } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import AchievementsPanel from './AchievementsPanel';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

export default function UnifiedMenu({ active, stats, currentQuote, recentMoods, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [languageExpanded, setLanguageExpanded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  useEffect(() => {
    axios.get(`${API}/admin/check`).then(r => setIsAdmin(r.data?.is_admin)).catch(() => {});
    axios.get(`${API}/inbox/unread-count`).then(r => setUnreadCount(r.data?.unread || 0)).catch(() => {});
  }, []);

  const handleLanguageChange = async (newLang) => {
    try {
      await axios.patch(`${API}/auth/update-profile`, { language: newLang });
      toast.success('Language updated!');
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      toast.error('Failed to update language');
    }
  };

  const navItems = [
    { id: 'profile', icon: User, label: 'Profile', path: '/profile' },
    { id: 'check-in', icon: Heart, label: 'Record Feelings', path: '/check-in' },
    { id: 'dashboard', icon: Home, label: t('home'), path: '/dashboard' },
    { id: 'music', icon: Music, label: t('music'), path: '/music' },
    { id: 'journal', icon: BookOpen, label: t('journal'), path: '/journal' },
    { id: 'voice-chat', icon: null, label: 'Chat & Voice', path: '/voice-chat', customIcon: true },
    { id: 'history', icon: BarChart3, label: t('history'), path: '/history' },
    { id: 'community', icon: Users, label: t('community'), path: '/community' },
    { id: 'notifications', icon: Bell, label: 'Notifications', path: '/notifications' },
    { id: 'inbox', icon: Inbox, label: 'Inbox', path: '/inbox', badge: unreadCount },
    { id: 'about', icon: Info, label: 'About Us', path: '/about' },
    ...(isAdmin ? [{ id: 'admin', icon: Shield, label: 'Admin Panel', path: '/admin' }] : [])
  ];

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Single Hamburger Button - Top Right */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all"
        style={{ width: '44px', height: '44px' }}
      >
        <div className="flex flex-col gap-1 items-center justify-center">
          <div className="w-5 h-0.5 bg-hunter-green"></div>
          <div className="w-5 h-0.5 bg-hunter-green"></div>
          <div className="w-5 h-0.5 bg-hunter-green"></div>
        </div>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold shadow-sm">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[51]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Unified Slide-out Menu */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-[52] transform transition-transform duration-200 overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-200 p-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
            Menu
          </h2>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-warm-charcoal/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Language Selection - Expandable */}
          <div>
            <button
              onClick={() => setLanguageExpanded(!languageExpanded)}
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-stone-50 active:bg-stone-100 text-warm-charcoal/70"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-hunter-green" />
                <span className="font-medium text-sm">Language</span>
              </div>
              <span className="text-xs text-warm-charcoal/50">
                {languages.find(l => l.code === language)?.flag} {languageExpanded ? '▲' : '▼'}
              </span>
            </button>
            
            {languageExpanded && (
              <div className="mt-2 grid grid-cols-2 gap-2 pl-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`p-2 rounded-lg text-left transition-all ${
                      language === lang.code
                        ? 'bg-hunter-green text-white'
                        : 'bg-stone-50 hover:bg-stone-100 text-warm-charcoal/70'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base">{lang.flag}</span>
                      <span className="text-xs font-medium">{lang.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Navigation Section */}
          <div>
            <h3 className="text-xs font-semibold text-warm-charcoal/50 uppercase tracking-wider mb-2">Navigate</h3>
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.path)}
                    data-testid={`menu-item-${item.id}`}
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-hunter-green text-white shadow-md' 
                        : 'hover:bg-stone-50 active:bg-stone-100 text-warm-charcoal/70'
                    }`}
                  >
                    {item.customIcon ? (
                      <div className="flex items-center gap-0.5 w-4">
                        <MessageCircle className="w-3 h-3" strokeWidth={isActive ? 2 : 1.5} />
                        <Mic className="w-3 h-3" strokeWidth={isActive ? 2 : 1.5} />
                      </div>
                    ) : (
                      <Icon className="w-4 h-4" strokeWidth={isActive ? 2 : 1.5} />
                    )}
                    <span className="font-medium text-sm">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[18px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
              
              {/* Contact Us */}
              <a
                href="mailto:hello@mindmymojo.com?subject=Mind My Mojo Feedback"
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all hover:bg-stone-50 text-warm-charcoal/70"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium text-sm">Contact Us</span>
              </a>
              
              {/* Logout - Consistent with other menu items */}
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  localStorage.removeItem('mojo_token');
                  sessionStorage.clear();
                  localStorage.clear();
                  document.cookie.split(";").forEach(c => document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"));
                  window.location.href = '/';
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all hover:bg-red-50 text-red-600"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-medium text-sm">Logout</span>
              </a>
            </div>
          </div>

          {/* Quote of the Moment */}
          {currentQuote && (
            <div className="bg-gradient-to-br from-sage/10 to-white p-4 rounded-xl border border-sage/20">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-hunter-green" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-warm-charcoal/70">
                  {t('quote_of_moment')}
                </h3>
              </div>
              <p className="text-xs text-warm-charcoal/80 italic leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
                "{currentQuote}"
              </p>
            </div>
          )}

          {/* Stats */}
          {stats && stats.total_entries > 0 && (
            <div className="bg-white p-4 rounded-xl border border-stone-200">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-hunter-green" />
                <h3 className="text-xs font-semibold uppercase tracking-wider">Stats</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-hunter-green/5 p-2 rounded-lg">
                  <p className="text-xs text-warm-charcoal/60">Check-ins</p>
                  <p className="text-xl font-bold text-hunter-green">{stats.total_entries}</p>
                </div>
                <div className="bg-sage/10 p-2 rounded-lg">
                  <p className="text-xs text-warm-charcoal/60">Streak</p>
                  <p className="text-xl font-bold text-hunter-green">{stats.streak || 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* Achievements */}
          {stats && (
            <div className="bg-white p-4 rounded-xl border border-stone-200">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-hunter-green" />
                <h3 className="text-xs font-semibold uppercase tracking-wider">Achievements</h3>
              </div>
              <AchievementsPanel stats={stats} />
            </div>
          )}

          {/* Recent Moods */}
          {recentMoods && recentMoods.length > 0 && (
            <div className="bg-white p-4 rounded-xl border border-stone-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-hunter-green" />
                <h3 className="text-xs font-semibold uppercase tracking-wider">Recent</h3>
              </div>
              <div className="space-y-2">
                {recentMoods.slice(0, 5).map((mood, idx) => (
                  <div key={idx} className="p-2 bg-stone-50 rounded-lg">
                    <p className="text-xs font-medium text-warm-charcoal">
                      {mood.specific_feeling || mood.primary_category || mood.mood_name}
                    </p>
                    <p className="text-[10px] text-warm-charcoal/50 mt-0.5">
                      {new Date(mood.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
